import { createContext, useContext, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertCircle, UploadCloud, Clapperboard, Scissors, PenLine, Sparkles, Image as ImageIcon, FileText, Wand2, CalendarDays, Youtube, Smartphone } from "lucide-react";
import api from "../lib/api";
import { playDoneSound } from "../lib/sound";
import { useQuota } from "./QuotaContext";

const CHUNK = 8 * 1024 * 1024;
const UploadCtx = createContext(null);
export const useUploads = () => useContext(UploadCtx) || {};

const probeVideo = (fileUrl) => new Promise((resolve) => {
  const v = document.createElement("video");
  v.preload = "metadata"; v.muted = true; v.src = fileUrl;
  v.onloadedmetadata = () => {
    const duration = v.duration || 8;
    v.currentTime = Math.min(1, duration / 2);
    v.onseeked = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 320; canvas.height = 180;
      try {
        canvas.getContext("2d").drawImage(v, 0, 0, 320, 180);
        canvas.toBlob((blob) => resolve({ duration, thumb: blob }), "image/jpeg", 0.7);
      } catch (e) { resolve({ duration, thumb: null }); }
    };
    setTimeout(() => resolve({ duration, thumb: null }), 4000);
  };
  v.onerror = () => resolve({ duration: 8, thumb: null });
});

const probeMeta = async (f, isVideo) => {
  if (!isVideo) return { duration: 5, thumb: null };
  const objUrl = URL.createObjectURL(f);
  const r = await probeVideo(objUrl);
  URL.revokeObjectURL(objUrl);
  return { duration: r.duration, thumb: r.thumb };
};

const uploadDirect = async (projectId, f, isVideo) => {
  const { duration, thumb } = await probeMeta(f, isVideo);
  const fd = new FormData();
  fd.append("file", f);
  fd.append("title", f.name.replace(/\.[^.]+$/, ""));
  fd.append("duration", String(duration));
  if (thumb) fd.append("thumbnail", thumb, "thumb.jpg");
  const { data } = await api.post(`/projects/${projectId}/broll/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
};

const postChunkWithRetry = async (projectId, uploadId, idx, chunk, tries = 5) => {
  let lastErr;
  for (let attempt = 0; attempt < tries; attempt++) {
    try {
      return await api.post(`/projects/${projectId}/broll/upload/chunk`, chunk, {
        params: { upload_id: uploadId, index: idx },
        headers: { "Content-Type": "application/octet-stream" },
      });
    } catch (e) {
      lastErr = e;
      const st = e?.response?.status;
      // 404 = sesión perdida (no recuperable). Resto (502/503/504/red) = transitorio → reintentar.
      if (st === 404) throw e;
      if (attempt < tries - 1) await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
    }
  }
  throw lastErr;
};

const uploadChunked = async (projectId, f, isVideo, onPct) => {
  const { duration, thumb } = await probeMeta(f, isVideo);
  const { data: init } = await api.post(`/projects/${projectId}/broll/upload/init`, { filename: f.name, total_size: f.size });
  const uploadId = init.upload_id;
  let sent = 0, idx = 0;
  for (let start = 0; start < f.size; start += CHUNK, idx++) {
    const chunk = f.slice(start, Math.min(f.size, start + CHUNK));
    await postChunkWithRetry(projectId, uploadId, idx, chunk);
    sent += chunk.size;
    onPct && onPct(Math.round((sent / f.size) * 100));
  }
  const fd = new FormData();
  fd.append("upload_id", uploadId);
  fd.append("duration", String(duration));
  fd.append("title", f.name.replace(/\.[^.]+$/, ""));
  if (thumb) fd.append("thumbnail", thumb, "thumb.jpg");
  const { data } = await api.post(`/projects/${projectId}/broll/upload/complete`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
};

export function UploadProvider({ children }) {
  const { t } = useTranslation();
  const { quota } = useQuota() || {};
  const [uploads, setUploads] = useState([]); // {id, name, pct, status, projectId}
  const reloadCbs = useRef({});

  const setU = (id, patch) => setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  const removeU = (id) => setUploads((prev) => prev.filter((u) => u.id !== id));

  const registerReload = useCallback((projectId, cb) => {
    if (!projectId || !cb) return () => {};
    const set = (reloadCbs.current[projectId] = reloadCbs.current[projectId] || new Set());
    set.add(cb);
    return () => { set.delete(cb); };
  }, []);
  const fireReload = (projectId) => {
    (reloadCbs.current[projectId] || []).forEach((cb) => { try { cb(); } catch (e) {} });
  };

  const startUploads = useCallback(async (projectId, files) => {
    const media = (files || []).filter((f) => f.type.startsWith("image/") || f.type.startsWith("video/"));
    if (!media.length) { toast.error(t("mf.selectImageOrVideo")); return; }
    const limitMb = quota?.max_upload_mb || 250;
    const limitBytes = limitMb * 1024 * 1024;
    const valid = [];
    for (const f of media) {
      if (f.size > limitBytes) {
        toast.error(t("mf.tooBig", { name: f.name, size: Math.ceil(f.size / (1024 * 1024)), limit: limitMb }));
      } else valid.push(f);
    }
    if (!valid.length) return;
    let ok = 0, failed = 0;
    const batchTotal = valid.length;
    for (let i = 0; i < valid.length; i++) {
      const f = valid[i];
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const isVideo = f.type.startsWith("video/");
      setUploads((prev) => [...prev, { id, name: f.name, pct: 0, status: "uploading", projectId, batchCurrent: i + 1, batchTotal }]);
      try {
        // Subida directa siempre por ahora (uploadChunked queda disponible sin usar:
        // pendiente de rehacerse con multipart upload nativo de S3 antes de reactivarla,
        // ver BACKLOG.md).
        setU(id, { pct: 60 }); await uploadDirect(projectId, f, isVideo);
        ok++;
        setU(id, { pct: 100, status: "done" });
        fireReload(projectId);
        setTimeout(() => removeU(id), 2500);
      } catch (e) {
        failed++;
        setU(id, { status: "error" });
        setTimeout(() => removeU(id), 7000);
      }
    }
    if (ok) { playDoneSound(); toast.success(t("mf.addedMany", { count: ok })); }
    if (failed) toast.error(t("mf.failedMany", { count: failed }));
    fireReload(projectId);
  }, [t, quota?.max_upload_mb]);

  const hasActive = (projectId) => uploads.some((u) => u.projectId === projectId && u.status === "uploading");
  const activePct = (projectId) => {
    const a = uploads.find((u) => u.projectId === projectId && u.status === "uploading");
    return a ? a.pct : null;
  };
  const activeBatch = (projectId) => {
    const a = uploads.find((u) => u.projectId === projectId && u.status === "uploading");
    return a && a.batchTotal > 1 ? { current: a.batchCurrent, total: a.batchTotal } : null;
  };

  // ---- CORTE de B-roll (mismo patrón: estado global + panel flotante + polling robusto) ----
  const [cuts, setCuts] = useState([]); // {id, projectId, name, pct, phase, done, total, status}
  const cutTimers = useRef({});
  const patchCut = (id, patch) => setCuts((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const removeCut = (id) => setCuts((prev) => prev.filter((c) => c.id !== id));

  const _pollCut = (projectId, fileId) => {
    let attempts = 0, fails = 0, lastReload = 0, lastDone = 0;
    const tick = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/projects/${projectId}/broll/files/${fileId}/cut-status`);
        fails = 0;
        if (data.status === "completo") {
          patchCut(fileId, { pct: 100, phase: "done", status: "done" });
          playDoneSound(); toast.success(t("mf.cutDone")); fireReload(projectId);
          setTimeout(() => removeCut(fileId), 2500); return;
        }
        if (data.status === "error") {
          patchCut(fileId, { status: "error" });
          toast.error(data.error || t("mf.cutFail")); fireReload(projectId);
          setTimeout(() => removeCut(fileId), 8000); return;
        }
        if ((data.parts_done || 0) > lastDone) {
          lastDone = data.parts_done; const now = Date.now();
          if (now - lastReload > 4000) { lastReload = now; fireReload(projectId); }
        }
        setCuts((prev) => prev.map((c) => (c.id === fileId
          ? { ...c, pct: Math.max(c.pct || 0, data.progress ?? 0), phase: data.phase || "preparing", done: data.parts_done || 0, total: data.parts_total || 0 }
          : c)));
      } catch (e) {
        if (e?.response?.status === 404) {
          patchCut(fileId, { pct: 100, phase: "done", status: "done" });
          playDoneSound(); toast.success(t("mf.cutDone")); fireReload(projectId);
          setTimeout(() => removeCut(fileId), 2500); return;
        }
        fails++;
        // Varios fallos seguidos (502/red) → no dejar el % congelado: se marca error.
        if (fails >= 8) {
          patchCut(fileId, { status: "error" });
          toast.error(t("mf.cutFail")); fireReload(projectId);
          setTimeout(() => removeCut(fileId), 8000); return;
        }
      }
      if (attempts > 800) { patchCut(fileId, { status: "error" }); setTimeout(() => removeCut(fileId), 8000); return; }
      cutTimers.current[fileId] = setTimeout(tick, 3000);
    };
    cutTimers.current[fileId] = setTimeout(tick, 3000);
  };

  const startCut = useCallback(async (projectId, fileId, name, opts = {}) => {
    setCuts((prev) => (prev.some((c) => c.id === fileId)
      ? prev.map((c) => (c.id === fileId ? { ...c, status: "cutting" } : c))
      : [...prev, { id: fileId, projectId, name: name || "Video", pct: 0, phase: "preparing", done: 0, total: 0, status: "cutting" }]));
    if (!opts.resume) {
      try { await api.post(`/projects/${projectId}/broll/files/${fileId}/cut`); toast.info(t("mf.cutStarted")); }
      catch (e) {
        patchCut(fileId, { status: "error" });
        toast.error(e?.response?.data?.detail || t("mf.cutFail"));
        setTimeout(() => removeCut(fileId), 8000); return;
      }
    }
    _pollCut(projectId, fileId);
  }, [t]);

  const isCutting = (fileId) => cuts.some((c) => c.id === fileId && c.status === "cutting");
  const getCut = (fileId) => cuts.find((c) => c.id === fileId && c.status === "cutting");

  // ---- COMPLETAR MATERIAL ("lo que falta") — mismo patrón que los cortes ----
  // Sin esto el trabajo era invisible fuera de la pantalla del 1-clic: podía estar buscando
  // material 40 minutos sin que nada en la cabecera lo indicara.
  const [fills, setFills] = useState([]); // {id: projectId, projectId, name, pct, detail, status}
  const fillTimers = useRef({});
  const patchFill = (pid, patch) => setFills((prev) => prev.map((f) => (f.id === pid ? { ...f, ...patch } : f)));
  const removeFill = (pid) => setFills((prev) => prev.filter((f) => f.id !== pid));

  const _pollFill = (projectId) => {
    if (fillTimers.current[projectId]) clearTimeout(fillTimers.current[projectId]);
    let attempts = 0, fails = 0;
    const tick = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/projects/${projectId}/broll/fill-gaps/status`);
        fails = 0;
        if (!data.active) {
          // El aviso dice la VERDAD sobre si el vídeo ya se puede renderizar. Antes cantaba
          // siempre "Material completado" aunque quedaran escenas sin cubrir, y el usuario se
          // topaba después con un render que decía justo lo contrario (ago-2026).
          const falta = data.uncovered || 0;
          patchFill(projectId, { status: "done", pct: data.progress || 100, detail: "" });
          playDoneSound();
          if (falta > 0) toast(t("oc.fillGapsPartial", { secs: falta }), { icon: "⚠️" });
          else toast.success(t("oc.fillGapsFinished"));
          fireReload(projectId);
          setTimeout(() => removeFill(projectId), 4000); return;
        }
        patchFill(projectId, { status: "filling", pct: data.progress || 0, detail: data.detail || "",
                               done: data.clips || 0, total: data.total || 0 });
      } catch (e) {
        fails++;
        if (fails >= 8) {
          patchFill(projectId, { status: "error" });
          setTimeout(() => removeFill(projectId), 8000); return;
        }
      }
      if (attempts > 2000) { removeFill(projectId); return; }
      fillTimers.current[projectId] = setTimeout(tick, 3000);
    };
    fillTimers.current[projectId] = setTimeout(tick, 3000);
  };

  // `resume`: al abrir la app con un completado ya en marcha (recarga, otra pestaña), se
  // reengancha el sondeo sin volver a lanzar la petición.
  const startFill = useCallback(async (projectId, name, opts = {}) => {
    setFills((prev) => (prev.some((f) => f.id === projectId)
      ? prev.map((f) => (f.id === projectId ? { ...f, status: "filling" } : f))
      : [...prev, { id: projectId, projectId, name: name || "Vídeo", pct: 0, detail: "", status: "filling" }]));
    if (!opts.resume) {
      try { await api.post(`/projects/${projectId}/broll/fill-gaps`); }
      catch (e) {
        removeFill(projectId);
        toast.error(e?.response?.data?.detail || t("oc.fillGapsError"));
        return false;
      }
    }
    _pollFill(projectId);
    return true;
  }, [t]);

  const isFilling = (projectId) => fills.some((f) => f.id === projectId && f.status === "filling");

  // ---- CREAR SHORT ----
  // Mismo motivo que el resto: generar un short escribe guion propio, sintetiza narración y
  // compone vídeo vertical — tarda minutos, y hasta ahora solo se veía dentro de la tarjeta
  // "Crear short" de esa pantalla concreta (ShortsCard tiene su propio sondeo local). Si el
  // usuario cambiaba de pestaña, no quedaba ninguna señal de que siguiera trabajando.
  const [shorts, setShorts] = useState([]); // {id: projectId, name, status, pct}
  const shortTimers = useRef({});
  const patchShort = (pid, patch) => setShorts((prev) => prev.map((s) => (s.id === pid ? { ...s, ...patch } : s)));
  const removeShort = (pid) => setShorts((prev) => prev.filter((s) => s.id !== pid));

  const _pollShort = (projectId) => {
    if (shortTimers.current[projectId]) clearTimeout(shortTimers.current[projectId]);
    let fails = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/shorts/status`);
        fails = 0;
        if (data.status === "completo") {
          patchShort(projectId, { status: "done", pct: 100 });
          playDoneSound(); toast.success(t("render.shortsReady")); fireReload(projectId);
          setTimeout(() => removeShort(projectId), 4000); return;
        }
        if (data.status === "error") {
          patchShort(projectId, { status: "error" });
          toast.error(t("render.shortsError", { error: data.error || "" })); fireReload(projectId);
          setTimeout(() => removeShort(projectId), 8000); return;
        }
        patchShort(projectId, { status: "procesando", pct: data.progress || 0 });
      } catch (e) {
        // Tolerante a cortes de red, igual que el resto: el short sigue generándose en el
        // servidor aunque no podamos preguntar por su estado.
        if (++fails >= 8) { removeShort(projectId); return; }
      }
      shortTimers.current[projectId] = setTimeout(tick, 3000);
    };
    tick();
  };

  const startShort = useCallback((projectId, name) => {
    setShorts((prev) => (prev.some((s) => s.id === projectId)
      ? prev
      : [...prev, { id: projectId, name: name || "Short", status: "procesando", pct: 0 }]));
    _pollShort(projectId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- SUBIDA A YOUTUBE ----
  // Mismo patrón que el resto de trabajos de fondo, y por el mismo motivo: subir un vídeo tarda
  // minutos y hasta ahora solo se veía dentro de la pantalla de publicación de ESE vídeo. Si el
  // usuario se iba a otra pantalla, no había ninguna señal de que su vídeo estuviera subiendo.
  const [ytUploads, setYtUploads] = useState([]); // {id: projectId, name, status}
  const ytTimers = useRef({});
  const removeYt = (pid) => setYtUploads((prev) => prev.filter((y) => y.id !== pid));

  const _pollYt = (projectId) => {
    if (ytTimers.current[projectId]) clearTimeout(ytTimers.current[projectId]);
    let fails = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/youtube/upload-status`);
        fails = 0;
        if (data.status === "done") {
          setYtUploads((prev) => prev.map((y) => (y.id === projectId ? { ...y, status: "done" } : y)));
          playDoneSound(); toast.success(t("dl.ytUploadedToast")); fireReload(projectId);
          setTimeout(() => removeYt(projectId), 5000); return;
        }
        if (data.status === "error") {
          setYtUploads((prev) => prev.map((y) => (y.id === projectId ? { ...y, status: "error" } : y)));
          setTimeout(() => removeYt(projectId), 8000); return;
        }
      } catch (e) {
        // Tolerante a cortes de red: la subida sigue en el servidor aunque no podamos preguntar.
        if (++fails >= 8) { removeYt(projectId); return; }
      }
      ytTimers.current[projectId] = setTimeout(tick, 4000);
    };
    tick();
  };

  const startYoutubeUpload = useCallback((projectId, name) => {
    setYtUploads((prev) => (prev.some((y) => y.id === projectId)
      ? prev
      : [...prev, { id: projectId, name: name || "Vídeo", status: "uploading" }]));
    _pollYt(projectId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- RENDER de vídeo (mismo patrón: estado global + panel flotante + polling robusto) ----
  const [renders, setRenders] = useState([]); // {id: projectId, projectId, name, status, pct, step, raw}
  const renderTimers = useRef({});
  const patchRender = (pid, patch) => setRenders((prev) => prev.map((r) => (r.id === pid ? { ...r, ...patch } : r)));
  const removeRender = (pid) => setRenders((prev) => prev.filter((r) => r.id !== pid));

  const _stepLabel = (data) => {
    const cur = (data.steps || []).find((s) => s.status === "en_proceso");
    return cur ? cur.name : "";
  };

  const _pollRender = (projectId) => {
    if (renderTimers.current[projectId]) clearTimeout(renderTimers.current[projectId]);
    let attempts = 0, fails = 0;
    const tick = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/projects/${projectId}/render/status`);
        fails = 0;
        if (data.status === "completo") {
          patchRender(projectId, { status: "done", pct: 100, step: "", raw: data });
          playDoneSound(); toast.success(t("render.successToast")); fireReload(projectId);
          setTimeout(() => removeRender(projectId), 4000); return;
        }
        if (data.status === "error") {
          patchRender(projectId, { status: "error", raw: data });
          toast.error(data.error || t("render.errorToast")); fireReload(projectId);
          setTimeout(() => removeRender(projectId), 8000); return;
        }
        if (data.status === "en_cola") {
          // Sin return: sigue el flujo normal hasta el final de tick(), que reprograma el
          // siguiente sondeo — así el tope de 3000 intentos (línea de abajo) también cubre el
          // tiempo en cola, no solo el renderizado real.
          patchRender(projectId, { status: "en_cola", pct: 0, step: "", raw: data });
        } else {
          const items = data.detail_items || [];
          const stepText = items.find((it) => it.key.startsWith("_global"))?.text || items[0]?.text || _stepLabel(data);
          patchRender(projectId, { status: "rendering", pct: data.progress || 0, step: stepText, activity: items, raw: data });
        }
      } catch (e) {
        fails++;
        if (fails >= 10) {
          patchRender(projectId, { status: "error" });
          toast.error(t("render.errorToast")); fireReload(projectId);
          setTimeout(() => removeRender(projectId), 8000); return;
        }
      }
      if (attempts > 3000) { patchRender(projectId, { status: "error" }); setTimeout(() => removeRender(projectId), 8000); return; }
      renderTimers.current[projectId] = setTimeout(tick, 1500);
    };
    renderTimers.current[projectId] = setTimeout(tick, 1500);
  };

  const startRender = useCallback(async (projectId, name, opts = {}) => {
    // `raw` arranca con un esqueleto "recién empezado" en vez de null: si no, el componente
    // (render = globalRender?.raw || project?.render) caía al project.render del render ANTERIOR
    // ya completo (100%, todos los pasos en verde) hasta que llegaba la primera respuesta del
    // polling — un re-render se veía como "no pasa nada, ya está al 100%" durante ese hueco.
    const freshRaw = { status: "rendering", progress: 0, steps: [], log: [], detail_items: [] };
    setRenders((prev) => (prev.some((r) => r.id === projectId)
      ? prev.map((r) => (r.id === projectId ? { ...r, status: "rendering", pct: 0, step: "", raw: freshRaw } : r))
      : [...prev, { id: projectId, projectId, name: name || "Vídeo", status: "rendering", pct: 0, step: "", raw: freshRaw }]));
    if (!opts.resume) {
      try {
        const { data } = await api.post(`/projects/${projectId}/render`);
        // El estado optimista de arriba asume "rendering" (el caso común, sin cola) para dar
        // feedback instantáneo sin esperar la red — si en realidad tocó esperar, se corrige
        // aquí mismo en cuanto se sabe, en vez de dejar que lo arregle el primer sondeo 1.5s
        // después.
        if (data?.status === "en_cola") patchRender(projectId, { status: "en_cola", pct: 0, step: "" });
        toast.info(t("render.started"));
      } catch (e) {
        patchRender(projectId, { status: "error" });
        toast.error(e?.response?.data?.detail || t("render.errorToast"));
        setTimeout(() => removeRender(projectId), 8000); return false;
      }
    }
    _pollRender(projectId);
    return true;
  }, [t]);

  // "En cola" cuenta como ocupado igual que "rendering" para quien solo necesita saber "no
  // dejes lanzar otra cosa" (useStepBusy, OneClickProducer) — la distinción entre las dos solo
  // le importa a la pantalla de Renderizar, que lee el status real (ver TabRenderizar.jsx).
  const isRendering = (projectId) => renders.some((r) => r.id === projectId && (r.status === "rendering" || r.status === "en_cola"));
  const getRender = (projectId) => renders.find((r) => r.id === projectId);

  // ---- PRODUCCIÓN EN 1 CLIC (mismo patrón: estado global + píldora en el header, sobrevive a
  // cambios de pestaña/canal). El pipeline entero (guion → voz → material → composición) tarda
  // 2-4 min; sin esta píldora global el usuario solo veía el progreso si se quedaba quieto en
  // OneClickProducer. Al llegar a "rendering" se entrega el seguimiento a startRender (mismo
  // proyecto, ya no hace falta una píldora aparte) — el backend ya envía el email de "listo para
  // revisar" cuando llega a awaiting_approval, aquí solo avisamos con sonido + toast en el acto.
  const [oneClicks, setOneClicks] = useState([]); // {id: projectId, projectId, name, status, step, stepDetail}
  const ocTimers = useRef({});
  const ocNotified = useRef({});
  const patchOneClick = (pid, patch) => setOneClicks((prev) => prev.map((o) => (o.id === pid ? { ...o, ...patch } : o)));
  const removeOneClick = (pid) => setOneClicks((prev) => prev.filter((o) => o.id !== pid));

  const _pollOneClick = (projectId) => {
    if (ocTimers.current[projectId]) clearTimeout(ocTimers.current[projectId]);
    let attempts = 0, fails = 0;
    const tick = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/projects/${projectId}/one-click/status`);
        fails = 0;
        if (data.status === "awaiting_approval") {
          patchOneClick(projectId, { status: "awaiting_approval", step: data.step, stepDetail: data.step_detail });
          if (!ocNotified.current[projectId]) {
            ocNotified.current[projectId] = true;
            playDoneSound();
            toast.info(t("oc.readyToast", { name: oneClicks.find((o) => o.id === projectId)?.name || "" }));
          }
        } else if (data.status === "completed" || data.status === "dismissed") {
          delete ocNotified.current[projectId];
          removeOneClick(projectId);
          fireReload(projectId);
          return;
        } else if (data.status === "rendering") {
          delete ocNotified.current[projectId];
          removeOneClick(projectId);
          const name = oneClicks.find((o) => o.id === projectId)?.name;
          startRender(projectId, name, { resume: true });
          return;
        } else if (data.status === "error") {
          patchOneClick(projectId, { status: "error" });
          toast.error(data.error || t("oc.pipelineError")); fireReload(projectId);
          setTimeout(() => removeOneClick(projectId), 8000); return;
        } else {
          patchOneClick(projectId, { status: "en_curso", step: data.step, stepDetail: data.step_detail });
        }
      } catch (e) {
        fails++;
        if (fails >= 10) {
          patchOneClick(projectId, { status: "error" });
          toast.error(t("oc.pipelineError")); fireReload(projectId);
          setTimeout(() => removeOneClick(projectId), 8000); return;
        }
      }
      if (attempts > 800) { patchOneClick(projectId, { status: "error" }); setTimeout(() => removeOneClick(projectId), 8000); return; }
      ocTimers.current[projectId] = setTimeout(tick, 4000);
    };
    ocTimers.current[projectId] = setTimeout(tick, 4000);
  };

  const startOneClick = useCallback((projectId, name) => {
    setOneClicks((prev) => (prev.some((o) => o.id === projectId)
      ? prev.map((o) => (o.id === projectId ? { ...o, status: "en_curso" } : o))
      : [...prev, { id: projectId, projectId, name: name || "Vídeo", status: "en_curso", step: "script", stepDetail: "" }]));
    delete ocNotified.current[projectId];
    _pollOneClick(projectId);
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- GENERACIÓN/MEJORA de guion IA (mismo patrón: estado global + panel flotante + polling robusto) ----
  // Ambas comparten el mismo campo de progreso (script_gen.status/words) en el proyecto;
  // solo cambian el endpoint que las dispara y los mensajes ("generating" vs "improving").
  const [scripts, setScripts] = useState([]); // {id: projectId, projectId, name, status, kind, words, segments}
  const scriptTimers = useRef({});
  const patchScript = (pid, patch) => setScripts((prev) => prev.map((s) => (s.id === pid ? { ...s, ...patch } : s)));
  const removeScript = (pid) => setScripts((prev) => prev.filter((s) => s.id !== pid));

  const _pollScript = (projectId, kind) => {
    if (scriptTimers.current[projectId]) clearTimeout(scriptTimers.current[projectId]);
    const doneKey = kind === "improve" ? "guion.improvedToast" : "videos.scriptDone";
    const failKey = kind === "improve" ? "guion.improveError" : "videos.scriptFail";
    const progressStatus = kind === "improve" ? "improving" : "generating";
    let attempts = 0, fails = 0;
    const tick = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/projects/${projectId}`);
        fails = 0;
        const sg = data.script_gen || {};
        if (sg.status === "completo") {
          patchScript(projectId, { status: "done", words: sg.words || 0 });
          playDoneSound();
          if (kind === "improve" && data.quality?.previous_score != null) {
            toast.success(t("guion.improvedToastScore", { prev: data.quality.previous_score, score: data.quality.score }));
          } else {
            toast.success(t(doneKey, { name: data.name }));
          }
          fireReload(projectId);
          setTimeout(() => removeScript(projectId), 3000); return;
        }
        if (sg.status === "error") {
          patchScript(projectId, { status: "error" });
          toast.error(sg.error || t(failKey)); fireReload(projectId);
          setTimeout(() => removeScript(projectId), 8000); return;
        }
        patchScript(projectId, { status: progressStatus, words: sg.words || 0, segments: sg.segments || 0 });
      } catch (e) {
        fails++;
        if (fails >= 10) {
          patchScript(projectId, { status: "error" });
          toast.error(t(failKey)); fireReload(projectId);
          setTimeout(() => removeScript(projectId), 8000); return;
        }
      }
      if (attempts > 800) { patchScript(projectId, { status: "error" }); setTimeout(() => removeScript(projectId), 8000); return; }
      scriptTimers.current[projectId] = setTimeout(tick, 3000);
    };
    scriptTimers.current[projectId] = setTimeout(tick, 3000);
  };

  const startScript = useCallback((projectId, name) => {
    setScripts((prev) => (prev.some((s) => s.id === projectId)
      ? prev.map((s) => (s.id === projectId ? { ...s, status: "generating", kind: "generate", words: 0, segments: 0 } : s))
      : [...prev, { id: projectId, projectId, name: name || "Guion", status: "generating", kind: "generate", words: 0, segments: 0 }]));
    _pollScript(projectId, "generate");
  }, [t]);

  const startScriptImprove = useCallback(async (projectId, name) => {
    setScripts((prev) => (prev.some((s) => s.id === projectId)
      ? prev.map((s) => (s.id === projectId ? { ...s, status: "improving", kind: "improve", words: 0, segments: 0 } : s))
      : [...prev, { id: projectId, projectId, name: name || "Guion", status: "improving", kind: "improve", words: 0, segments: 0 }]));
    try {
      await api.post(`/projects/${projectId}/improve-script`);
      toast.success(t("quality.improvingToast"));
    } catch (e) {
      patchScript(projectId, { status: "error" });
      toast.error(e?.response?.data?.detail || t("guion.improveError"));
      setTimeout(() => removeScript(projectId), 8000); return false;
    }
    _pollScript(projectId, "improve");
    return true;
  }, [t]);

  // ---- ANÁLISIS de guion: la extracción principal es síncrona (POST /script), pero si hay
  // evaluación de calidad pendiente, esta sigue en segundo plano sin campo de estado propio
  // (solo se detecta por la aparición de project.quality) — se sondea igual que antes en
  // TabGuion.jsx, pero ahora de forma global para que sobreviva a cambios de pestaña.
  const _pollQuality = (projectId) => {
    if (scriptTimers.current[projectId]) clearTimeout(scriptTimers.current[projectId]);
    let attempts = 0, fails = 0;
    const tick = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/projects/${projectId}`);
        fails = 0;
        if (data.quality) {
          patchScript(projectId, { status: "done" });
          playDoneSound(); toast.success(t("guion.qualityToast", { score: data.quality.score })); fireReload(projectId);
          setTimeout(() => removeScript(projectId), 3000); return;
        }
      } catch (e) {
        fails++;
        if (fails >= 10) { patchScript(projectId, { status: "error" }); setTimeout(() => removeScript(projectId), 8000); return; }
      }
      if (attempts > 22) { patchScript(projectId, { status: "done" }); setTimeout(() => removeScript(projectId), 2000); return; }
      scriptTimers.current[projectId] = setTimeout(tick, 4000);
    };
    scriptTimers.current[projectId] = setTimeout(tick, 4000);
  };

  const startScriptAnalyze = useCallback(async (projectId, name, scriptText) => {
    setScripts((prev) => (prev.some((s) => s.id === projectId)
      ? prev.map((s) => (s.id === projectId ? { ...s, status: "analyzing", kind: "analyze" } : s))
      : [...prev, { id: projectId, projectId, name: name || "Guion", status: "analyzing", kind: "analyze" }]));
    try {
      const { data } = await api.post(`/projects/${projectId}/script`, { script: scriptText });
      if (data.quality_pending) {
        patchScript(projectId, { status: "evaluating" });
        _pollQuality(projectId);
      } else {
        patchScript(projectId, { status: "done" });
        setTimeout(() => removeScript(projectId), 2000);
      }
      return data;
    } catch (e) {
      patchScript(projectId, { status: "error" });
      setTimeout(() => removeScript(projectId), 8000);
      throw e;
    }
  }, []);

  // ---- MINIATURA con IA: el endpoint es síncrono (sin campo de progreso en el proyecto),
  // así que la píldora sigue directamente la promesa del POST en vez de hacer polling.
  const [thumbnails, setThumbnails] = useState([]); // {id: projectId, projectId, name, status}
  const patchThumb = (pid, patch) => setThumbnails((prev) => prev.map((th) => (th.id === pid ? { ...th, ...patch } : th)));
  const removeThumb = (pid) => setThumbnails((prev) => prev.filter((th) => th.id !== pid));

  const startThumbnail = useCallback(async (projectId, name, count = 1) => {
    setThumbnails((prev) => (prev.some((th) => th.id === projectId)
      ? prev.map((th) => (th.id === projectId ? { ...th, status: "generating" } : th))
      : [...prev, { id: projectId, projectId, name: name || "Miniatura", status: "generating" }]));
    try {
      const { data } = await api.post(`/projects/${projectId}/thumbnail/generate`, { count });
      patchThumb(projectId, { status: "done" });
      playDoneSound(); fireReload(projectId);
      setTimeout(() => removeThumb(projectId), 2500);
      return data;
    } catch (e) {
      patchThumb(projectId, { status: "error" });
      setTimeout(() => removeThumb(projectId), 8000);
      throw e;
    }
  }, []);

  // ---- CALENDARIO con IA: igual que la miniatura, el endpoint es síncrono (analiza
  // competidores + genera N ideas de título con IA y las guarda, todo en la misma request) —
  // la píldora sigue la promesa del POST, sin polling. Antes esto solo tenía un spinner local
  // en el botón de CalendarPanel: si el usuario cambiaba de pestaña mientras corría (puede
  // tardar igual que un guion, por el análisis de competidores + la llamada IA), no había
  // ninguna señal de que siguiera trabajando en segundo plano.
  const [calendars, setCalendars] = useState([]); // {id: channelId, channelId, name, status}
  const patchCalendar = (cid, patch) => setCalendars((prev) => prev.map((c) => (c.id === cid ? { ...c, ...patch } : c)));
  const removeCalendar = (cid) => setCalendars((prev) => prev.filter((c) => c.id !== cid));

  const startCalendarGenerate = useCallback(async (channelId, name, body) => {
    setCalendars((prev) => (prev.some((c) => c.id === channelId)
      ? prev.map((c) => (c.id === channelId ? { ...c, status: "generating" } : c))
      : [...prev, { id: channelId, channelId, name: name || "Calendario", status: "generating" }]));
    try {
      const { data } = await api.post(`/channels/${channelId}/calendar/generate`, body);
      patchCalendar(channelId, { status: "done" });
      playDoneSound(); fireReload(channelId);
      setTimeout(() => removeCalendar(channelId), 2500);
      return data;
    } catch (e) {
      patchCalendar(channelId, { status: "error" });
      setTimeout(() => removeCalendar(channelId), 8000);
      throw e;
    }
  }, []);

  return (
    <UploadCtx.Provider value={{ uploads, startUploads, registerReload, hasActive, activePct, activeBatch, cuts, startCut, isCutting, getCut, startRender, isRendering, getRender, renders, scripts, startScript, startScriptImprove, startScriptAnalyze, thumbnails, startThumbnail, oneClicks, startOneClick, calendars, startCalendarGenerate, fills, startFill, isFilling, ytUploads, startYoutubeUpload, shorts, startShort }}>
      {children}
    </UploadCtx.Provider>
  );
}

export function TasksBar() {
  const { t } = useTranslation();
  const { uploads = [], cuts = [], renders = [], scripts = [], thumbnails = [], oneClicks = [], calendars = [], fills = [], ytUploads = [], shorts = [] } = useUploads();
  // `accent: "youtube"` pinta la píldora en rojo YouTube: es la única tarea que sale de la
  // aplicación hacia fuera —publica algo en un sitio público— y conviene que se distinga de un
  // vistazo del resto de trabajos internos.
  const ytItems = ytUploads.map((y) => ({
    id: `yt-${y.id}`, name: y.name, pct: null, status: y.status, icon: "youtube", accent: "youtube",
    label: y.status === "uploading" ? t("dl.ytUploadingPill") : null,
  }));
  const fillItems = fills.map((f) => ({
    id: `fill-${f.id}`, name: f.name, pct: f.pct, status: f.status === "filling" ? "uploading" : f.status, icon: "cut",
    label: f.status === "filling"
      ? (f.detail || t("oc.fillGaps")) + (f.total ? ` · ${f.done}s/${f.total}s` : "")
      : null,
  }));
  const cutItems = cuts.map((c) => ({
    id: `cut-${c.id}`, name: c.name, pct: c.pct, status: c.status === "cutting" ? "uploading" : c.status, icon: "cut",
    label: c.status === "cutting" ? `${t(`clip.cutPhase_${c.phase || "preparing"}`)} ${c.pct}%${c.total ? ` · ${c.done}/${c.total}` : ""}` : null,
  }));
  const renderItems = renders.map((r) => {
    const parallel = (r.activity || []).filter((it) => !it.key.startsWith("_global")).length;
    const extra = parallel > 1 ? ` (+${parallel - 1})` : "";
    return {
      id: `render-${r.id}`, name: r.name, pct: r.pct, status: r.status === "rendering" ? "uploading" : r.status, icon: "render",
      label: r.status === "rendering" ? `${r.step || t("render.overallProgress")}${extra} · ${r.pct}%` : null,
    };
  });
  const scriptItems = scripts.map((s) => {
    const active = s.status === "generating" || s.status === "improving" || s.status === "analyzing" || s.status === "evaluating";
    const isImprove = s.kind === "improve";
    const isAnalyze = s.kind === "analyze";
    const label = s.status === "improving" ? t("quality.improving")
      : s.status === "analyzing" ? t("guion.analyzing")
      : s.status === "evaluating" ? t("guion.evaluating")
      : s.status === "generating" ? t("videos.scriptProgress", { words: s.words || 0 })
      : null;
    return {
      id: `script-${s.id}`, name: s.name, pct: null, status: active ? "uploading" : s.status,
      icon: isImprove ? "improve" : isAnalyze ? "analyze" : "script",
      label: active ? label : null,
    };
  });
  const shortItems = shorts.map((s) => ({
    id: `short-${s.id}`, name: s.name, pct: s.pct,
    status: s.status === "procesando" ? "uploading" : s.status, icon: "short",
    label: s.status === "procesando" ? `${t("render.shortsGenerating")} · ${s.pct || 0}%` : null,
  }));
  const thumbItems = thumbnails.map((th) => ({
    id: `thumb-${th.id}`, name: th.name, pct: null, status: th.status === "generating" ? "uploading" : th.status, icon: "thumb",
    label: th.status === "generating" ? t("dl.aiThumbGenerating") : null,
  }));
  const calendarItems = calendars.map((c) => ({
    id: `calendar-${c.id}`, name: c.name, pct: null, status: c.status === "generating" ? "uploading" : c.status, icon: "calendar",
    label: c.status === "generating" ? t("calp.generatingBtn") : null,
  }));
  const oneClickItems = oneClicks.map((o) => ({
    id: `oc-${o.id}`, name: o.name, pct: null,
    status: o.status === "en_curso" ? "uploading" : o.status === "awaiting_approval" ? "ready" : o.status,
    icon: "oneclick",
    label: o.status === "en_curso" ? (o.stepDetail || t(`oc.step.${o.step}`, { defaultValue: o.step }))
      : o.status === "awaiting_approval" ? t("oc.step.awaiting_approval") : null,
  }));
  const items = [...ytItems, ...oneClickItems, ...scriptItems, ...calendarItems, ...thumbItems, ...shortItems, ...renderItems, ...fillItems, ...uploads.map((u) => ({ ...u, icon: "upload" })), ...cutItems];
  if (!items.length) return null;
  return (
    <div className="border-t border-slate-100 bg-slate-50/70">
      <div data-testid="floating-uploads" className="max-w-7xl mx-auto px-4 sm:px-8 py-2 flex items-center gap-2 overflow-x-auto overflow-y-hidden vb-scroll">
        {items.map((u) => (
          <div key={u.id} data-testid={`upload-item-${u.status}`}
            className={`flex items-center gap-2 shrink-0 border rounded-full pl-2.5 pr-3 py-1.5 shadow-sm min-w-[180px] max-w-[320px] ${
              u.accent === "youtube" && u.status === "uploading" ? "bg-red-50 border-red-300"
                : u.status === "ready" ? "bg-amber-50 border-amber-300" : "bg-white border-slate-200"}`}>
            {u.status === "uploading" && (u.icon === "youtube"
              ? <Youtube size={15} className="text-red-600 shrink-0 animate-pulse" />
              : u.icon === "render"
              ? <Clapperboard size={15} className="text-blue-600 shrink-0 animate-pulse" />
              : u.icon === "cut"
              ? <Scissors size={15} className="text-amber-600 shrink-0" />
              : u.icon === "script"
              ? <PenLine size={15} className="text-purple-600 shrink-0 animate-pulse" />
              : u.icon === "improve"
              ? <Sparkles size={15} className="text-amber-500 shrink-0 animate-pulse" />
              : u.icon === "thumb"
              ? <ImageIcon size={15} className="text-violet-600 shrink-0 animate-pulse" />
              : u.icon === "calendar"
              ? <CalendarDays size={15} className="text-blue-500 shrink-0 animate-pulse" />
              : u.icon === "analyze"
              ? <FileText size={15} className="text-blue-500 shrink-0 animate-pulse" />
              : u.icon === "oneclick"
              ? <Wand2 size={15} className="text-orange-500 shrink-0 animate-pulse" />
              : u.icon === "short"
              ? <Smartphone size={15} className="text-violet-600 shrink-0 animate-pulse" />
              : <UploadCloud size={15} className="text-teal-600 shrink-0" />)}
            {u.status === "ready" && <CheckCircle2 size={15} className="text-amber-600 shrink-0" />}
            {u.status === "done" && <CheckCircle2 size={15} className="text-green-600 shrink-0" />}
            {u.status === "error" && <AlertCircle size={15} className="text-rose-600 shrink-0" />}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="min-w-0 shrink truncate text-xs font-medium text-slate-700">{u.name}</span>
                <span
                  title={u.status === "uploading" || u.status === "ready" ? (u.label || `${u.pct}%`) : undefined}
                  className={`min-w-0 shrink truncate text-[11px] ${u.status === "ready" ? "text-amber-700 font-semibold" : "text-slate-500"}`}>
                  {u.status === "uploading" ? (u.label || `${u.pct}%`)
                    : u.status === "ready" ? u.label
                    : u.status === "done" ? t("mf.upDone") : t("mf.upError")}
                </span>
              </div>
              {u.status === "uploading" && typeof u.pct === "number" && (
                <div className="mt-1 h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-300 ${u.icon === "cut" ? "bg-amber-500" : u.icon === "upload" ? "bg-teal-500" : "bg-blue-600"}`} style={{ width: `${u.pct}%` }} />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
